import { useState } from "react";
import {
  CalendarClockIcon,
  DownloadIcon,
  FileBarChart2Icon,
  PlayIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";

import {
  useCreateSchedule,
  useDeleteSchedule,
  useGenerateReport,
  useReportJobs,
  useReportSchedules,
  useRunScheduleNow,
  useUpdateSchedule,
  type ReportFormat,
  type ReportFrequency,
  type ReportSchedule,
  type ReportType,
} from "@/api/reports";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/api/client";
import { cn, formatDate } from "@/lib/utils";

const REPORT_TYPES: { value: ReportType; label: string }[] = [
  { value: "branch_compliance", label: "Branch Compliance" },
  { value: "camera_uptime", label: "Camera Uptime" },
  { value: "exception_report", label: "Exception Report" },
  { value: "alert_summary", label: "Alert Summary" },
  { value: "audit_summary", label: "Audit Summary" },
];

const FORMATS: { value: ReportFormat; label: string }[] = [
  { value: "pdf", label: "PDF" },
  { value: "excel", label: "Excel" },
  { value: "csv", label: "CSV" },
];

type Tab = "generate" | "schedule";

// A27 — download an authenticated report via the axios `api` client (carries the bearer
// token) instead of window.open (a bare navigation that sends no Authorization header →
// 401 on this auth-gated endpoint). Fetch the blob, then trigger a client-side download.
async function downloadReportJob(jobId: string) {
  const res = await api.get(`/api/v1/reports/jobs/${jobId}/download`, {
    responseType: "blob",
  });
  const cd = (res.headers["content-disposition"] as string | undefined) ?? "";
  const raw = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd)?.[1];
  const fname = raw ? decodeURIComponent(raw) : `report-${jobId}`;
  const objUrl = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}

function DownloadJobButton({ jobId }: { jobId: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await downloadReportJob(jobId);
        } catch {
          alert("Download failed — please retry.");
        } finally {
          setBusy(false);
        }
      }}
    >
      <DownloadIcon className="h-3.5 w-3.5" />
    </Button>
  );
}

function statusVariant(s: string): "online" | "warning" | "error" | "offline" {
  if (s === "complete") return "online";
  if (s === "failed") return "error";
  if (s === "queued" || s === "generating") return "warning";
  return "offline";
}

function GenerateTab() {
  const generate = useGenerateReport();
  const { data: jobs } = useReportJobs();
  const [reportType, setReportType] = useState<ReportType>("branch_compliance");
  const [format, setFormat] = useState<ReportFormat>("pdf");
  const [date, setDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [threshold, setThreshold] = useState(80);

  const onSubmit = async () => {
    const parameters: Record<string, unknown> = { date };
    if (reportType === "exception_report") {
      parameters.uptime_threshold = threshold;
      parameters.recording_threshold = threshold;
    }
    await generate.mutateAsync({
      report_type: reportType, format, parameters,
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-lg font-semibold">Generate report</h2>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label>Type</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={reportType}
                onChange={(e) => setReportType(e.target.value as ReportType)}
              >
                {REPORT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Format</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={format}
                onChange={(e) => setFormat(e.target.value as ReportFormat)}
              >
                {FORMATS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Date</Label>
              <Input
                type="date" value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>
          {reportType === "exception_report" && (
            <div className="space-y-1">
              <Label>Uptime / Recording threshold ({threshold} %)</Label>
              <input
                type="range" min={0} max={100} step={1}
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className="w-full"
              />
            </div>
          )}
          <div>
            <Button onClick={onSubmit} disabled={generate.isPending}>
              <PlayIcon className="mr-2 h-4 w-4" />
              {generate.isPending ? "Queuing…" : "Generate now"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="border-b p-4">
            <h3 className="font-semibold">Recent jobs</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Format</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-left">Size</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {(jobs?.items ?? []).map((j) => (
                <tr key={j.id} className="border-b last:border-0">
                  <td className="px-3 py-2">{j.report_type}</td>
                  <td className="px-3 py-2 uppercase">{j.format}</td>
                  <td className="px-3 py-2">
                    <Badge variant={statusVariant(j.status)}>{j.status}</Badge>
                    {j.error_message && (
                      <span className="ml-2 text-xs text-destructive">{j.error_message}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{formatDate((j.created_at as any)?.local ?? j.created_at)}</td>
                  <td className="px-3 py-2 text-xs">
                    {j.file_size_bytes
                      ? `${(j.file_size_bytes / 1024).toFixed(1)} KB`
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {j.status === "complete" && <DownloadJobButton jobId={j.id} />}
                  </td>
                </tr>
              ))}
              {(jobs?.items ?? []).length === 0 && (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                    No jobs yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}


function ScheduleSheet({
  open, onClose, editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: ReportSchedule | null;
}) {
  const create = useCreateSchedule();
  const update = useUpdateSchedule();
  const [name, setName] = useState(editing?.name ?? "");
  const [reportType, setReportType] = useState<ReportType>(
    editing?.report_type ?? "branch_compliance",
  );
  const [format, setFormat] = useState<ReportFormat>(editing?.format ?? "pdf");
  const [frequency, setFrequency] = useState<ReportFrequency>(
    editing?.frequency ?? "weekly",
  );
  const [day, setDay] = useState(editing?.schedule_day ?? 0);
  const [hour, setHour] = useState(editing?.schedule_hour ?? 8);
  const [emails, setEmails] = useState(
    (editing?.recipient_emails ?? []).join(", "),
  );
  const [enabled, setEnabled] = useState(editing?.is_enabled ?? true);

  if (!open) return null;
  const submit = async () => {
    const recipient_emails = emails
      .split(",").map((s) => s.trim()).filter(Boolean);
    if (editing) {
      await update.mutateAsync({
        id: editing.id,
        patch: {
          name, format, frequency,
          schedule_day: frequency === "weekly" || frequency === "monthly"
            ? day : undefined,
          schedule_hour: hour,
          recipient_emails,
          is_enabled: enabled,
        },
      });
    } else {
      await create.mutateAsync({
        name, report_type: reportType, format, frequency,
        schedule_day: frequency === "weekly" || frequency === "monthly"
          ? day : undefined,
        schedule_hour: hour,
        recipient_emails,
        is_enabled: enabled,
      });
    }
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto">
        <CardContent className="space-y-3 p-4">
          <h3 className="text-lg font-semibold">
            {editing ? "Edit schedule" : "New schedule"}
          </h3>
          <div className="space-y-1">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {!editing && (
            <div className="space-y-1">
              <Label>Report type</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={reportType}
                onChange={(e) => setReportType(e.target.value as ReportType)}
              >
                {REPORT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Format</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={format}
                onChange={(e) => setFormat(e.target.value as ReportFormat)}
              >
                {FORMATS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Frequency</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as ReportFrequency)}
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="on_demand">On demand</option>
              </select>
            </div>
          </div>
          {(frequency === "weekly" || frequency === "monthly") && (
            <div className="space-y-1">
              <Label>{frequency === "weekly" ? "Day of week" : "Day of month"}</Label>
              <Input
                type="number"
                min={frequency === "weekly" ? 0 : 1}
                max={frequency === "weekly" ? 6 : 28}
                value={day}
                onChange={(e) => setDay(Number(e.target.value))}
              />
            </div>
          )}
          <div className="space-y-1">
            <Label>Hour (tenant time, 0-23)</Label>
            <Input
              type="number" min={0} max={23} value={hour}
              onChange={(e) => setHour(Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label>Recipient emails (comma-separated)</Label>
            <Input
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="ops@bank.local, audit@bank.local"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox" checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enabled
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" disabled={!name} onClick={submit}>
              {editing ? "Save" : "Create"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ScheduleTab() {
  const { data } = useReportSchedules();
  const del = useDeleteSchedule();
  const runNow = useRunScheduleNow();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<ReportSchedule | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setEditing(null); setSheetOpen(true); }}>
          <PlusIcon className="mr-2 h-4 w-4" />New schedule
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Frequency</th>
                <th className="px-3 py-2 text-left">Next run</th>
                <th className="px-3 py-2 text-left">Recipients</th>
                <th className="px-3 py-2 text-left">Enabled</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((s) => (
                <tr key={s.id} className="border-b last:border-0">
                  <td className="px-3 py-2 font-medium">{s.name}</td>
                  <td className="px-3 py-2 text-xs">{s.report_type}</td>
                  <td className="px-3 py-2 text-xs">{s.frequency}</td>
                  <td className="px-3 py-2 text-xs">
                    {formatDate((s.next_run_at as any)?.local ?? "—")}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {s.recipient_emails.length} addr.
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={s.is_enabled ? "online" : "offline"}>
                      {s.is_enabled ? "on" : "off"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => runNow.mutate(s.id)}
                      >
                        <PlayIcon className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setEditing(s); setSheetOpen(true); }}
                      >Edit</Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={async () => {
                          if (!confirm(`Delete "${s.name}"?`)) return;
                          await del.mutateAsync(s.id);
                        }}
                      >
                        <Trash2Icon className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {(data?.items ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                    No schedules yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <ScheduleSheet
        open={sheetOpen}
        editing={editing}
        onClose={() => setSheetOpen(false)}
      />
    </div>
  );
}

export default function ReportBuilder() {
  const [tab, setTab] = useState<Tab>("generate");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <FileBarChart2Icon className="h-6 w-6" />
          Reports
        </h1>
        <p className="text-sm text-muted-foreground">
          Build compliance, uptime, exception, alert, and audit reports.
        </p>
      </div>
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab("generate")}
          className={cn(
            "px-3 py-2 text-sm",
            tab === "generate"
              ? "border-b-2 border-primary font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >Generate</button>
        <button
          onClick={() => setTab("schedule")}
          className={cn(
            "flex items-center gap-1 px-3 py-2 text-sm",
            tab === "schedule"
              ? "border-b-2 border-primary font-medium text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <CalendarClockIcon className="h-4 w-4" />Schedules
        </button>
      </div>
      {tab === "generate" && <GenerateTab />}
      {tab === "schedule" && <ScheduleTab />}
    </div>
  );
}
